import { useEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { Form } from 'antd'
import Text from 'shared/components/typography/Text'
import { formKeys } from '../../DonationForm/constants'
import FiatProviderSelect from '../../FiatProviderSelect'
import TypeBlock from '../TypeBlock'
import Option from '../TypeBlock/Option'
import { getVisibleOptions } from '../utils'
import ShowAllButton from '../ShowAllButton'
import { formatFiatProviderToOption } from './utils'
import { lang } from './lang'
import MethodsIcon from './methods.svg?react'
import { PaymentType } from '@/pages/Donation/api/streamer/types'
import { FiatProvider, FormValues, OptionType } from '@/pages/Donation/types'

type Props = {
  options?: FiatProvider[]
  isOptionsLoading?: boolean
}

export default function Fiat({ options = [], isOptionsLoading }: Props) {
  const form = Form.useFormInstance<FormValues>()
  const [isOptionsModalOpen, setOptionsModalOpen] = useState(false)
  const selectedOption = Form.useWatch<FormValues[typeof formKeys.selectedFiatProvider]>(formKeys.selectedFiatProvider)
  const paymentType = Form.useWatch<FormValues[typeof formKeys.paymentType]>(formKeys.paymentType)
  const handleSelectMethod = (value: FiatProvider | null) => form.setFieldValue(formKeys.selectedFiatProvider, value)
  const lastSelectedOptionRef = useRef<FiatProvider | null>(selectedOption)

  const handleOptionsModalOpen = () => setOptionsModalOpen(true)
  const handleOptionsModalClose = () => setOptionsModalOpen(false)

  const handleSelectQuickProvider = (value: OptionType) => {
    const selectedOption = options.find(option => option.payment_method === value.code) ?? null
    handleSelectMethod(selectedOption)
  }

  useEffect(() => {
    const shouldSetDefaultMethod =
      !isOptionsLoading && !options?.some(option => option.payment_method === selectedOption?.payment_method)

    if (shouldSetDefaultMethod) {
      const defaultSelectedOption: FiatProvider | null =
        options.find(option => option.payment_method === lastSelectedOptionRef?.current?.payment_method) ??
        options?.[0] ??
        null
      handleSelectMethod(defaultSelectedOption)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, isOptionsLoading])

  useEffect(() => {
    if (selectedOption) {
      lastSelectedOptionRef.current = selectedOption
    }
  }, [selectedOption])

  const quickOptions = useMemo(
    () =>
      options
        .slice(0, 3)
        .map(option => formatFiatProviderToOption(option))
        .filter(Boolean) as OptionType[],
    [options]
  )

  const visibleOptions = getVisibleOptions(quickOptions, formatFiatProviderToOption(selectedOption))

  let rightContent = null

  if (visibleOptions.length < options.length && !isOptionsLoading) {
    rightContent = <ShowAllButton onClick={handleOptionsModalOpen} />
  } else {
    rightContent = <MethodsIcon />
  }

  return (
    <>
      <FiatProviderSelect
        isOpen={isOptionsModalOpen}
        onClose={handleOptionsModalClose}
        options={options}
        selectedProvider={selectedOption}
        onSelectProvider={handleSelectMethod}
      />
      <Form.Item name={formKeys.selectedFiatProvider} noStyle />
      <TypeBlock<OptionType>
        isOptionsLoading={isOptionsLoading}
        loadingText={<FormattedMessage {...lang.loading} />}
        paymentType={PaymentType.Fiat}
        isSelected={paymentType === PaymentType.Fiat}
        visibleOptions={visibleOptions}
        title={<FormattedMessage {...lang.fiat} />}
        rightContent={rightContent}
        renderOption={option => (
          <Option
            code={option.code}
            icon={option.icon}
            onClick={() => handleSelectQuickProvider(option)}
            isSelected={selectedOption?.payment_method === option.code}
          >
            <Text fontSize={14} fontType="body" type="primary">
              {option.name}
            </Text>
          </Option>
        )}
      />
    </>
  )
}
